<template>
  <div class="map-page-container">
    <img v-lazy="headerImage" height="100%" v-if="isMobile">

    <!-- ✅ MOBILE LAYOUT: Placeholder is first, map is second -->
    <!-- This placeholder shows only on mobile before the map is activated -->
    <div v-if="isMobile && !mapActiveOnMobile" @click="activateMap" class="map-placeholder">
<!--      <ResponsiveImage :image="headerImage" alt="Map of Tulum Clubs"></ResponsiveImage>-->

      <div class="placeholder-overlay">
        <span>Click to Load Interactive Map</span>
        <div
          v-show="isMobile || mapActiveOnMobile"
          id="google-map-container"
          ref="mapContainer"
        ></div>
      </div>
    </div>

    <!-- The actual map container -->
    <div
      v-show="!isMobile || mapActiveOnMobile"
      id="google-map-container"
      ref="mapContainer"
    ></div>

<!--    <pre>{{venues}}</pre>-->
<!--    <div v-for="venue in venues">-->
<!--      <ClubCard :title="venue.title"-->
<!--                :content="venue.metadata.content"-->

<!--                :image="venue.metadata.main_image.imgix_url"></ClubCard>-->
<!--    </div>-->

    <div class="map-controls">
      <!-- On mobile, the header image is inside the scrollable controls -->
      <ResponsiveImage  :image="headerImage"  v-show="!isMobile"></ResponsiveImage>

      <header >
        <h1 class="tulum-party-title">Tulum Nightlife Map</h1>
      </header>
      <hr>
      <div class="card" style="padding: 10px">
        <p>Visually explore every club in Tulum. Use the filters to find your perfect spot.</p>

      </div>

      <!-- Search & Main Filters -->
      <div >
        <input
          class="centered"
          style="width: 90%"
          v-model="searchQuery"
          @input="activateMap"
          type="text"
          placeholder="Search name, vibe, music, DJs..."
        />
        <div>
          <input type="checkbox" id="open-now-checkbox" v-model="filterOpenNow" @change="activateMap" />
          <label for="open-now-checkbox">Open Now</label>
        </div>
      </div>

      <!-- Multi-Select Filters -->
      <Collapse>
        <CollapseItem title="Music Type">
          <div class="control-section">
            <div class="filter-group">
              <div v-for="music in filterOptions.music" :key="music" class="filter-checkbox">
                <input type="checkbox" :id="`music-${music}`" :value="music" v-model="selectedMusic" @change="activateMap" />
                <label :for="`music-${music}`">{{ music }}</label>
              </div>
            </div>
          </div>
        </CollapseItem>

        <CollapseItem title="Good For">
          <div class="control-section">
            <div class="filter-group">
              <div v-for="goodFor in filterOptions.goodFor" :key="goodFor" class="filter-checkbox">
                <input type="checkbox" :id="`goodFor-${goodFor}`" :value="goodFor" v-model="selectedGoodFor" @change="activateMap" />
                <label :for="`goodFor-${goodFor}`">{{ goodFor }}</label>
              </div>
            </div>
          </div>
        </CollapseItem>
      </Collapse>

      <div class="control-section">
        <button @click="clearFilters" class="clear-filters-btn">Clear All Filters</button>
      </div>
    </div>
  </div>
</template>

<script>
import CLUBS_CONFIG from '../../config/tulum-clubs-config.json';
import Collapse from '@/components/Collapse/Collapse';
import CollapseItem from '@/components/Collapse/CollapseItem';
import ResponsiveImage from '@/components/HeadImage/ResponsiveImage';

export default {
  name: 'TulumClubsMap',
  components: {  ResponsiveImage, CollapseItem, Collapse },
  metaInfo() {
    // Meta info remains the same.
    const pageUrl = 'https://www.tulum.party/tulum-clubs-map';
    return {
      title: 'Interactive Map of Tulum Clubs & Nightlife',
      link: [{ rel: 'canonical', href: pageUrl }],
      meta: [
        { hid: 'description', name: 'description', content: 'Explore all Tulum clubs, beach parties, and nightlife venues on an interactive map. Filter by open now, area, price, music and more to plan your perfect night out.' },
        { hid: 'og:url', property: 'og:url', content: pageUrl },
        { hid: 'og:title', property: 'og:title', content: 'Interactive Map of Tulum Clubs & Nightlife' },
      ]
    };
  },
  data() {
    return {
      clubsConfig: CLUBS_CONFIG,
      headerImage: 'https://imgix.cosmicjs.com/873febb0-f401-11eb-aa23-db5c20501154-image.png',
      map: null,
      mapInitialized: false,
      markers: [],
      infoWindow: null,
      imageFolder: 'clubs-vagalume-tulum',
      CustomMarkerClass: null, // ✅ ADDED: To hold the class definition once map is loaded
      // State for mobile layout
      isMobile: false,
      mapActiveOnMobile: false,
      // Enhanced filter state
      searchQuery: '',
      filterOpenNow: false,
      selectedMusic: [],
      selectedGoodFor: []
    };
  },
  watch: {
    // Watch all filters to update the map reactively
    filteredClubs() {
      if (this.mapInitialized) {
        this.updateMarkers();
      }
    }
  },
  computed: {
    activeClubsList() {
      return Object.values(this.clubsConfig).filter(club => !club.closed && club.directions && club.directions.googleMap);
    },
    venues(){
        return this.$store.state.venues;
    },
    filterOptions() {
      const music = new Set();
      const goodFor = new Set();
      this.activeClubsList.forEach(club => {
        if (club.tags) {
          const musicTag = club.tags.find(t => t.label === 'MUSIC');
          if (musicTag && musicTag.value) {
            musicTag.value.split('/').forEach(m => music.add(m.trim().toUpperCase()));
          }
          const goodForTag = club.tags.find(t => t.label === 'GOOD FOR');
          if (goodForTag && goodForTag.value) goodFor.add(goodForTag.value.trim().toUpperCase());
        }
      });
      return {
        music: Array.from(music).sort(),
        goodFor: Array.from(goodFor).sort()
      };
    },
    filteredClubs() {
      const now = new Date(); // Current time for "Open Now" filter

      return this.activeClubsList.filter(club => {
        // 1. "Open Now" Filter
        if (this.filterOpenNow && !this.isClubOpen(club, now)) {
          return false;
        }

        // 2. Multi-Select Music Filter
        if (this.selectedMusic.length > 0) {
          const clubMusic = this.getTagValue(club.tags, 'MUSIC').toUpperCase();
          const hasMatch = this.selectedMusic.some(musicFilter => clubMusic.includes(musicFilter));
          if (!hasMatch) return false;
        }

        // 3. Multi-Select "Good For" Filter
        if (this.selectedGoodFor.length > 0) {
          const clubGoodFor = this.getTagValue(club.tags, 'GOOD FOR').toUpperCase();
          const hasMatch = this.selectedGoodFor.some(goodForFilter => clubGoodFor.includes(goodForFilter));
          if (!hasMatch) return false;
        }

        // 4. Enhanced Search Query Filter
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          const inName = club.name.toLowerCase().includes(query);
          const inCaption = club.caption && club.caption.toLowerCase().includes(query);
          const inTags = club.tags && club.tags.some(tag =>
            (tag.value && tag.value.toLowerCase().includes(query)) ||
            (tag.desc && tag.desc.toLowerCase().includes(query))
          );
          if (!(inName || inCaption || inTags)) return false;
        }

        return true; // If all filters pass, include the club
      });
    }
  },
  created() {
    // Check initial screen size
    this.checkIfMobile();
    this.$store.dispatch('getVenues',
      { start: 0, limit: 16 }).then(x => {
      // this.$store.dispatch('getLinks', { slug: 'tulum-clubs' });
      // this.clubsConfig = Object.fromEntries(Object.entries(clubsConfig).filter(([clubId, clubData]) => {
      //   return !clubData || clubData.closed !== true;
      // }));
      window.addEventListener('resize', this.checkIfMobile);
      window.history.scrollRestoration = 'manual';
    });
  },
  mounted() {
    // Only load map on mount for desktop
    if (!this.isMobile) {
      this.loadGoogleMapsAPI();
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkIfMobile);
  },
  methods: {
    checkIfMobile() {
      this.isMobile = window.innerWidth <= 900;
    },
    activateMap() {
      if (!this.mapInitialized) {
        this.mapActiveOnMobile = true;
        this.loadGoogleMapsAPI();
      }
    },
    loadGoogleMapsAPI() {
      if (this.mapInitialized) return; // Prevent multiple loads
      this.mapInitialized = true;

      const apiKey = 'AIzaSyCzWl07YrBfr0oF6RBikA9Wtmw3K2DKERc'; // Replace with your actual API key
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initMap`;
      script.async = true;
      script.defer = true;
      window.initMap = this.initializeMap;
      document.head.appendChild(script);
    },
    initializeMap() {
      // ✅ HELPER CLASS: Defined here, now that `google` object is guaranteed to exist.
      this.CustomMarkerClass = class CustomMarker extends google.maps.OverlayView {
        constructor(position, map, clubData, onClick) {
          super();
          this.position = position;
          this.clubData = clubData;
          this.onClick = onClick;

          this.container = document.createElement('div');
          this.container.classList.add('custom-marker');
          this.container.style.position = 'absolute';
          this.container.addEventListener('click', () => this.onClick(this));

          const img = document.createElement('img');
          img.src = this.clubData.logo;
          img.alt = `${this.clubData.name} logo`;
          img.style.width = '40px';
          img.style.height = '40px';
          img.style.borderRadius = '50%';
          img.style.border = '2px solid white';
          img.style.boxShadow = '0 2px 5px rgba(0,0,0,0.4)';
          this.container.appendChild(img);

          this.setMap(map);
        }
        onAdd() { this.getPanes().overlayMouseTarget.appendChild(this.container); }
        draw() {
          const overlayProjection = this.getProjection();
          const sw = overlayProjection.fromLatLngToDivPixel(this.position);
          this.container.style.left = (sw.x - 20) + 'px'; // Center the marker
          this.container.style.top = (sw.y - 20) + 'px';
        }
        onRemove() { if (this.container.parentElement) { this.container.parentElement.removeChild(this.container); } }
        setVisible(isVisible) { this.container.style.display = isVisible ? '' : 'none'; }
        setOpacity(isOpen) { this.container.classList.toggle('is-open', isOpen); this.container.classList.toggle('is-closed', !isOpen); }
        getPosition() { return this.position; }
      }


      const tulumCoords = { lat: 20.187, lng: -87.464 };
      this.map = new google.maps.Map(this.$refs.mapContainer, {
        center: tulumCoords,
        zoom: 12,
        mapId: 'TULUM_NIGHTLIFE_MAP_STYLE',
        styles: [ // Basic dark mode map style
          { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
          { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
          { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
          { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#263c3f" }] },
          { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#6b9a76" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
          { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#212a37" }] },
          { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca5b3" }] },
          { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#746855" }] },
          { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#1f2835" }] },
          { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#f3d19c" }] },
          { featureType: "transit", elementType: "geometry", stylers: [{ color: "#2f3948" }] },
          { featureType: "transit.station", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
          { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#515c6d" }] },
          { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#17263c" }] }
        ]
      });
      this.infoWindow = new google.maps.InfoWindow({
        content: '',
        backgroundColor: '#333',
        borderColor: '#555'
      });
      this.plotAllMarkers();
      this.updateMarkers(); // Initial marker visibility check
    },
    plotAllMarkers() {
      const now = new Date();
      this.activeClubsList.forEach(club => {
        const coords = this.extractCoordinates(club);
        if (coords) {
          const isOpen = this.isClubOpen(club, now);
          // ✅ UPDATED: Use the class now stored on the component instance
          const marker = new this.CustomMarkerClass(
            new google.maps.LatLng(coords.latitude, coords.longitude),
            this.map,
            club,
            (markerInstance) => {
              const content = this.generateInfoWindowContent(markerInstance.clubData);
              this.infoWindow.setContent(content);
              this.infoWindow.open(this.map, markerInstance);
              // this.imageFolder = 'clubs-'+ club.slug.split("/")[2];
            },

          );
          marker.setOpacity(isOpen);
          this.markers.push(marker);
        }
      });
    },
    updateMarkers() {
      const filteredClubIds = new Set(this.filteredClubs.map(c => c.slug));
      this.markers.forEach(marker => {
        const isVisible = filteredClubIds.has(marker.clubData.slug);
        marker.setVisible(isVisible);
      });
      if (this.infoWindow) {
        this.infoWindow.close();
      }
    },
    generateInfoWindowContent(club) {
      return `
            <div style="font-family: Arial, sans-serif; max-width: 250px; background-color: #2b2b2d; color: #f0f0f0; padding: 10px; border-radius: 5px;">
                <img src="${club.logo}" alt="${club.name}" style="width: 50px; height: 50px; object-fit: contain; float: left; margin-right: 15px; border-radius: 4px; background: #444;">
                <h3 style="margin: 0 0 5px 0; font-size: 1.2em; color: #fff;">${club.name}</h3>
                <p style="margin: 0 0 10px 0; font-size: 0.9em; color: #ccc;">${this.getTagValue(club.tags, 'MUSIC') || club.caption}</p>
                <div style="clear: both; padding-top: 10px;">
                    <span style="font-weight: bold; color: #d4af37; font-size: 1.1em;">${this.formatPriceRange(club.price)}</span>
                    <a href="${club.slug}" target="_blank" style="float: right; text-decoration: none; background-color: #555; color: white; padding: 6px 12px; border-radius: 4px; font-size: 0.9em;">View Details</a>
                </div>
            </div>
        `;
    },
    clearFilters() { this.searchQuery = ''; this.filterOpenNow = false; this.selectedMusic = []; this.selectedGoodFor = []; },
    isClubOpen(club, now) { if (!club.tags) return false; const jsDay = now.getDay(); const ourDaySpec = jsDay === 0 ? 7 : jsDay; const openDays = club.daysOpen || [1, 2, 3, 4, 5, 6, 7]; if (!openDays.includes(ourDaySpec)) { return false; } const openingSpecs = this.generateOpeningHours(club, true); const currentTime = now.getHours() * 100 + now.getMinutes(); for (const spec of openingSpecs) { const opens = parseInt(spec.opens.replace(':', ''), 10); let closes = parseInt(spec.closes.replace(':', ''), 10); if (closes < opens) { if (currentTime >= opens || currentTime < closes) { return true; } } else { if (currentTime >= opens && currentTime < closes) { return true; } } } return false; },
    getTagValue(tags, label) { if (!tags) return ''; const tag = tags.find(t => t.label === label); return tag ? tag.value : ''; },
    formatPriceRange(price) { return price >= 1 && price <= 4 ? '$'.repeat(price) : '$$$'; },
    extractCoordinates(club) {  return { latitude: parseFloat(club.directions.latitude), longitude: parseFloat(club.directions.longitude) } ;},
    generateOpeningHours(club, forCheck = false) { if (!club.tags) return []; const daySpecMap = {1:"Monday",2:"Tuesday",3:"Wednesday",4:"Thursday",5:"Friday",6:"Saturday",7:"Sunday"}; const openDays = club.daysOpen || [1, 2, 3, 4, 5, 6, 7]; const schemaDays = openDays.map(dayNum => daySpecMap[dayNum]); const parseTime = (str) => { if (!str || str.includes('--') || !str.match(/(\d+)(AM|PM)?/)) return null; str = str.toUpperCase().trim(); let [time, modifier] = str.match(/(\d+)(AM|PM)?/).slice(1); let [hours] = time.split(':'); hours = parseInt(hours, 10); if (modifier === 'PM' && hours < 12) hours += 12; if (modifier === 'AM' && hours === 12) hours = 0; return `${String(hours).padStart(2, '0')}:00`;}; const hours = []; const dayTag = club.tags.find(t => t.label === 'DAY'); const nightTag = club.tags.find(t => t.label === 'NIGHT'); if (dayTag && !dayTag.value.includes('--')) { const [opens, closes] = dayTag.value.split('-'); const opensTime = parseTime(opens); const closesTime = parseTime(closes); if (opensTime && closesTime) { hours.push({ dayOfWeek: schemaDays, opens: opensTime, closes: closesTime }); } } if (nightTag && !nightTag.value.includes('--')) { const [opens, closesRaw] = nightTag.value.split('-'); const opensTime = parseTime(opens); let closesTime = parseTime(closesRaw); if (closesRaw.toUpperCase().includes('LATE')) closesTime = '02:00'; else if (!closesTime) closesTime = '02:00'; if (opensTime && closesTime) { hours.push({ dayOfWeek: schemaDays, opens: opensTime, closes: closesTime }); } } return forCheck ? hours : hours.map(h => ({'@type':'OpeningHoursSpecification', dayOfWeek:h.dayOfWeek, opens:h.opens, closes:h.closes})); }
  }
};
</script>

<style scoped>
.map-page-container {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background-color: #1a1a1c;
  color: #f0f0f0;
}
.map-controls {
  width: 380px;
  flex-shrink: 0;
  padding: 0; /* Remove padding for edge-to-edge image */
  background-color: #2b2b2d;
  overflow-y: auto;
  border-right: 1px solid #444;
  order: 1; /* Controls on left for desktop */
}
.map-header {
  text-align: left;
  padding: 20px;
}
.map-header h1 { font-size: 2em;color: #fff; }
.map-header p { font-size: 1em; color: #aaa; }

.control-section {
  /*margin-bottom: 25px;*/
  padding: 0 20px; /* Add padding back to control sections */
}

#open-now-checkbox:checked { accent-color: #d4af37; } /* Gold color for checkbox */

.clear-filters-btn { background-color: #555; }
.clear-filters-btn:hover { background-color: #666; }

/* ✅ NEW Styles for Lazy Map Placeholder */
.map-placeholder {
  position: relative;
  cursor: pointer;
  order: 1; /* Map placeholder first on mobile */
  width: 100%;
}
.placeholder-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5em;
  font-weight: bold;
  text-align: center;
  transition: background-color 0.3s ease;
}
.map-placeholder:hover .placeholder-overlay {
  background-color: rgba(0, 0, 0, 0.7);
}


#google-map-container {
  width: 100%;
  height: 100%;
  order: 2; /* Map on right for desktop */
}

/* ✅ NEW Styles for Custom HTML Markers */
.custom-marker {
  background-color: #1a1818;
  transition: opacity 0.3s ease, filter 0.3s ease;
}
.custom-marker.is-closed {
  /*filter: grayscale(100%);*/
  border: 1px solid red;
  opacity: 0.6;
}
.custom-marker.is-open {
  filter: none;
  border: 1px solid red;

  opacity: 1;
}

@media (max-width: 900px) {
  .map-page-container {
    flex-direction: column;
    height: auto;
  }
  .map-controls {
    width: 100%;
    order: 2; /* Controls second on mobile */
    border-right: none;
  }
  #google-map-container, .map-placeholder {
    height: 60vh;
    order: 1; /* Map (or placeholder) first on mobile */
  }
}
</style>
